import React, { useEffect, useRef, useState, useLayoutEffect } from "react";
import { Tab, TabKeys } from "../../data/tabs";
import {
  mainFrame,
  mainFrameBackground,
  mainFrameBolts,
  mainFrameBottomFins,
  mainFrameEtchings,
  mainFrameFinCarvings,
  mainFrameFins,
  mainFrameMarkFade,
  mainFramePips,
  mainFrameTitle,
  mainFrameVents,
} from "../../utils/svg";
import styles from "./TabContainer.module.scss";

interface TabContainerProps {
  tabs: Tab[];
  selectedTab: TabKeys;
}

interface Dimensions {
  w: number;
  h: number;
  thin: number;
  thick: number;
  corner: number;
  morph: number;
}

// Full duration of the page-switch retraction wave: the midpoint between the
// too-fast original (~0.4s perceived) and the too-slow 1.8s pass, long
// enough that the stagger across the 9 pieces reads as a traveling wave.
const MORPH_DURATION_MS = 1100;

export const TabContainer: React.FC<TabContainerProps> = ({
  tabs,
  selectedTab,
}) => {
  const [dimensions, setDimensions] = useState<Dimensions>({
    w: 0,
    h: 0,
    thin: 0,
    thick: 0,
    corner: 0,
    morph: 0,
  });
  const currentTab = tabs.find((t) => t.key === selectedTab);
  // The pip row lags page switches: the gold pip may only move while the
  // markings are blacked out mid-pulse (mainFrameMarkFade's zero window,
  // morph 0.22..0.78) or when no pulse is running (morph 0, which also
  // covers reduced motion), so it fades out still on the old page and
  // fades back in already on the new one. Every pulse crosses the window
  // or ends at 0, so the swap can never be skipped.
  const [pipTab, setPipTab] = useState<Tab | undefined>();
  const tabRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<number | undefined>(undefined);
  // Page-switch morph pulse phase (0 = idle, advances 0..1 once per pulse),
  // advanced by wall-clock time so the wave lasts MORPH_DURATION_MS on any
  // refresh rate (and regardless of StrictMode's doubled rAF chains in dev).
  const morphRef = useRef(0);
  const morphLastRef = useRef(0);

  const mainFrameBackgroundColor = "#00000044";

  const calculateDimensions = (width: number, height: number): Dimensions => ({
    w: width,
    h: height,
    corner: width / 8,
    thick: width / 50,
    thin: width / 250,
    morph: 0,
  });

  const updateDimensions = (now: number): void => {
    if (tabRef.current) {
      const { clientWidth, clientHeight } = tabRef.current;
      const newDimensions = calculateDimensions(clientWidth, clientHeight);

      // Page-switch morph: the phase advances 0 -> 1 once per pulse over
      // MORPH_DURATION_MS; the svg generators run the retract-and-return
      // wave over the frame's fins and ticks as it progresses. dt is clamped
      // on both sides: capped at 64ms so throttled background tabs crawl
      // instead of skipping the wave, and floored at 0 because the first
      // frame after arming can carry a vsync-aligned timestamp slightly
      // OLDER than the performance.now() sampled while arming (frame times
      // are backdated under main-thread contention). An unfloored negative
      // dt used to drive the phase to <= 0 right here, ending the pulse
      // before a single frame rendered and stranding the gold pip: the swap
      // effect's idle test requires morphRef === 0, but the dead pulse left
      // it negative, so the pip kept pointing at the previous page.
      if (morphRef.current > 0) {
        const dt = Math.max(0, Math.min(64, now - morphLastRef.current));
        morphLastRef.current = now;
        const next = morphRef.current + dt / MORPH_DURATION_MS;
        morphRef.current = next >= 1 ? 0 : next;
      }

      if (
        Math.abs(newDimensions.w - dimensions.w) > 1 ||
        Math.abs(newDimensions.h - dimensions.h) > 1 ||
        morphRef.current > 0
      ) {
        setDimensions((prevDimensions) => ({
          w: prevDimensions.w + (newDimensions.w - prevDimensions.w) * 0.25,
          h: prevDimensions.h + (newDimensions.h - prevDimensions.h) * 0.2,
          corner:
            prevDimensions.corner +
            (newDimensions.corner - prevDimensions.corner) * 0.25,
          thick:
            prevDimensions.thick +
            (newDimensions.thick - prevDimensions.thick) * 0.25,
          thin:
            prevDimensions.thin +
            (newDimensions.thin - prevDimensions.thin) * 0.25,
          morph: morphRef.current,
        }));
        animationRef.current = requestAnimationFrame(updateDimensions);
      } else {
        setDimensions(newDimensions);
      }
    }
  };

  useEffect(() => {
    const handleResize = (): void => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      animationRef.current = requestAnimationFrame(updateDimensions);
    };

    window.addEventListener("resize", handleResize);
    return (): void => {
      window.removeEventListener("resize", handleResize);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
    // mount-only listener; updateDimensions is intentionally the mount closure (self-scheduling rAF)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Move the gold pip only while the pip row is invisible, or when truly
  // idle. "Idle" must check morphRef too: on a switch the layout effect
  // below arms morphRef synchronously, but dimensions.morph stays 0 until
  // the first rAF commit — without the ref guard this effect would treat
  // that gap as idle and swap the pip while it is still fully visible.
  // Layout effects run before passive ones, so by the time this fires for
  // the currentTab commit the pulse is already armed. Under reduced
  // motion the pulse never arms and the swap stays immediate.
  useEffect(() => {
    const m = dimensions.morph;
    const blackedOut = m >= 0.22 && m <= 0.78;
    const idle = m === 0 && morphRef.current === 0;
    if ((idle || blackedOut) && pipTab !== currentTab) {
      setPipTab(currentTab);
    }
  }, [dimensions.morph, currentTab, pipTab]);

  // measure and animate dimensions after DOM updates on tab change
  useLayoutEffect(() => {
    if (currentTab) {
      // Kick the page-switch morph pulse (skipped under reduced motion).
      if (!window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        morphRef.current = 0.001;
        morphLastRef.current = performance.now();
      }
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      animationRef.current = requestAnimationFrame(updateDimensions);
    }
    // mount-only listener; updateDimensions is intentionally the mount closure (self-scheduling rAF)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTab]);

  if (!currentTab) return null;

  const shownPipTab = pipTab ?? currentTab;

  return (
    <div className={styles["tab"]} ref={tabRef}>
      <div className={styles["frame"]}>
        <svg viewBox={`0 0 ${dimensions.w} ${dimensions.h}`}>
          <path
            d={mainFrameBackground(
              dimensions.w,
              dimensions.h,
              dimensions.corner,
              dimensions.thick,
              dimensions.thin,
            )}
            transform="translate(0 0)"
            fill={mainFrameBackgroundColor}
          />
          <path
            d={mainFrame(
              dimensions.w,
              dimensions.h,
              dimensions.corner,
              dimensions.thick,
              dimensions.thin,
              dimensions.morph,
            )}
            transform="translate(0 0)"
            fill="url(#grad-frame)"
          />
          {/* Traveling glint: the sweep gradient rides a full-frame rect,
              clipped to the frame shape and swept by a CSS transform
              (.frame-sweep). WebKit never runs SMIL animateTransform on
              gradientTransform, so the band must move with its element. */}
          <g clipPath="url(#frame-sweep-clip)">
            <rect
              className={styles["frame-sweep"]}
              x="0"
              y="0"
              width={dimensions.w}
              height={dimensions.h}
              fill="url(#grad-frame-sweep)"
            />
          </g>
          <path
            d={mainFrameTitle(
              dimensions.w,
              dimensions.h,
              dimensions.corner,
              dimensions.thick,
              dimensions.thin,
              dimensions.morph,
            )}
            transform="translate(0 0)"
            fill="url(#grad-frame-title)"
          />
          {/* Static engravings: etched vent grooves on the never-morphing
              rail sections plus the whisper etchings (plate datums,
              tallies, seams, chevrons) — depth, not geometry, dark and
              quiet. Under each dark mark sits the same shape nudged a
              hair down-right in the bolts' highlight tone: the groove's
              lower lip catching the frame's upper-left light, so
              carvings read chiseled into the plate instead of printed on
              it. Drawn AFTER the title plate: the band blade carries
              engravings too, and anything painted earlier would be
              buried under it. During the pulse the whole group dims
              inside the bolts' bracket (mainFrameMarkFade): out right
              after the heads, back right before them. */}
          <g opacity={mainFrameMarkFade(dimensions.morph)}>
            {mainFrameVents(
              dimensions.w,
              dimensions.h,
              dimensions.corner,
              dimensions.thick,
              dimensions.thin,
            ).map((mark, i) => (
              <React.Fragment key={i}>
                <path
                  d={mark}
                  transform={`translate(${dimensions.thin * 0.25} ${dimensions.thin * 0.25})`}
                  fill="#d6e8f4"
                  fillOpacity={0.22}
                />
                <path d={mark} fill="#121a1c" fillOpacity={0.5} />
              </React.Fragment>
            ))}
            {mainFrameEtchings(
              dimensions.w,
              dimensions.h,
              dimensions.corner,
              dimensions.thick,
              dimensions.thin,
            ).map((mark, i) => (
              <React.Fragment key={`e${i}`}>
                <path
                  d={mark}
                  transform={`translate(${dimensions.thin * 0.25} ${dimensions.thin * 0.25})`}
                  fill="#d6e8f4"
                  fillOpacity={0.22}
                />
                <path d={mark} fill="#121a1c" fillOpacity={0.5} />
              </React.Fragment>
            ))}
          </g>
          {mainFrameFins(
            dimensions.w,
            dimensions.h,
            dimensions.corner,
            dimensions.thick,
            dimensions.thin,
            dimensions.morph,
          ).map((fin, i) => (
            <path
              key={i}
              d={fin}
              transform="translate(0 0)"
              fill="url(#grad-frame-tick)"
            />
          ))}
          {mainFrameBottomFins(
            dimensions.w,
            dimensions.h,
            dimensions.corner,
            dimensions.thick,
            dimensions.thin,
            dimensions.morph,
          ).map((fin, i, arr) => (
            <path
              key={i}
              d={fin}
              transform="translate(0 0)"
              fill="url(#grad-frame-tick)"
              fillOpacity={Math.max(0.3, 1 - (arr.length - 1 - i) * 0.14)}
            />
          ))}
          {/* Fin carvings: inset smaller trapeziums on the title band's
              two mid blades. Two-layer inset paint like the
              etchings, but with the dark layer at half their 0.5 — a
              shallow relief, not a deep groove — and outside the markFade
              group: each shape follows its fin's wave order, so it stays
              welded to the metal at every phase instead of dimming. Drawn
              after the fins' edge-light overlays so the relief is never
              washed by them. */}
          {mainFrameFinCarvings(
            dimensions.corner,
            dimensions.thick,
            dimensions.thin,
            dimensions.morph,
          ).map((mark, i) => (
            <React.Fragment key={`c${i}`}>
              <path
                d={mark}
                transform={`translate(${dimensions.thin * 0.25} ${dimensions.thin * 0.25})`}
                fill="#d6e8f4"
                fillOpacity={0.22}
              />
              <path d={mark} fill="#121a1c" fillOpacity={0.25} />
            </React.Fragment>
          ))}
          {mainFrameBolts(
            dimensions.w,
            dimensions.h,
            dimensions.corner,
            dimensions.thick,
            dimensions.thin,
            dimensions.morph,
          ).map((bolt, i) =>
            bolt.r > 0.5 ? (
              // 50% group opacity: the heads read as worked metal that
              // belongs to the plate, not chrome studs on top of it.
              <g key={i} opacity={0.5}>
                <circle
                  cx={bolt.cx}
                  cy={bolt.cy}
                  r={bolt.r * 1.5}
                  fill="url(#grad-bolt-shadow)"
                />
                <circle
                  cx={bolt.cx}
                  cy={bolt.cy}
                  r={bolt.r}
                  fill="url(#grad-bolt)"
                />
              </g>
            ) : null,
          )}
          {/* Page pips: the three slash marks etched into the top bar's
              right end. The live page's pip warms to gold, the rest stay
              dark etch like the neighbouring engravings — a quiet
              indicator with no moving parts, each over the same chisel
              lip. Dims with the other markings inside the bolts' pulse
              bracket. */}
          <g opacity={mainFrameMarkFade(dimensions.morph)}>
            {mainFramePips(
              dimensions.w,
              dimensions.thick,
              dimensions.thin,
              tabs.length,
            ).map((pip, i) => {
              const active = tabs[i]?.key === shownPipTab.key;
              return (
                <React.Fragment key={i}>
                  <path
                    d={pip}
                    transform={`translate(${dimensions.thin * 0.25} ${dimensions.thin * 0.25})`}
                    fill="#d6e8f4"
                    fillOpacity={0.22}
                  />
                  <path
                    d={pip}
                    fill={active ? "#f8bd24" : "#121a1c"}
                    fillOpacity={active ? 0.7 : 0.5}
                  />
                </React.Fragment>
              );
            })}
          </g>
          {/* Title scales with the frame band (corner = w/8) like every other
              frame part; CSS no longer sets font-size, so this attribute is
              authoritative and the dimension lerp animates it smoothly. The
              family rides along as a presentation attribute for the same
              reason — both typographic facts stay next to the geometry that
              drives them. It has to be spelled out at all because the global
              body default is now the reading face, and the frame title is
              chrome; SVG attributes cannot read the Sass token. */}
          <text
            key={currentTab.key}
            className={styles["frame-title"]}
            x={dimensions.corner * 1.25}
            y={dimensions.corner - dimensions.corner * 0.3}
            fontSize={dimensions.corner * 0.5}
            fontFamily="ControlFreak, sans-serif"
            fill="#fff">
            {currentTab.title}
          </text>
          <defs>
            {/* Brushed-steel base: multi-stop metallic banding instead of the
                old flat two-stop fill. Hexes mirror $main-* in _colors.scss. */}
            <linearGradient
              id="grad-frame"
              x1="1"
              y1="0.35"
              x2="0"
              y2="0.65"
              gradientUnits="objectBoundingBox">
              <stop offset="0" stopColor="#576971" />
              <stop offset="0.3" stopColor="#8697a0" />
              <stop offset="0.52" stopColor="#a5b2b9" />
              <stop offset="0.75" stopColor="#6a7e8a" />
              <stop offset="1" stopColor="#8697a0" />
            </linearGradient>
            {/* Title plate: one continuous vertical ramp across the whole
                plate. All title pieces now share the same 3 * thin rise,
                so the bbox is shallow and the ramp spans every piece's
                full height — the lift therefore lives only in the top
                quarter, as a slightly lighter-grey lip fading straight
                into steel. Deliberately quiet: the band is an underline,
                not a second hero. */}
            <linearGradient
              id="grad-frame-title"
              x1="0"
              y1="0"
              x2="0"
              y2="1"
              gradientUnits="objectBoundingBox">
              <stop offset="0" stopColor="#a5b2b9" />
              <stop offset="0.25" stopColor="#96a3ab" />
              <stop offset="0.55" stopColor="#8b949c" />
              <stop offset="1" stopColor="#8697a0" />
            </linearGradient>
            {/* Edge-light overlay: transparent across most of the height so
                the ring's own brushed steel stays the body (the fin shapes
                are subpaths of mainFrame), lifting to a slightly lighter
                grey along the lower rim — a faint catch-light on the
                metal's edge, deliberately quieter than the old gold dip.
                fillOpacity dims only this layer per fin. */}
            <linearGradient
              id="grad-frame-tick"
              x1="0"
              y1="0"
              x2="0"
              y2="1"
              gradientUnits="objectBoundingBox">
              <stop offset="0" stopColor="#a5b2b9" stopOpacity="0" />
              <stop offset="0.55" stopColor="#a5b2b9" stopOpacity="0.08" />
              <stop offset="0.82" stopColor="#a5b2b9" stopOpacity="0.2" />
              <stop offset="0.95" stopColor="#becfda" stopOpacity="0.35" />
              <stop offset="1" stopColor="#d6e8f4" stopOpacity="0.5" />
            </linearGradient>
            {/* Bolt heads: a soft shadow halo emanating evenly around each
                dome seats it into the plate, then an offset radial highlight
                rolls through steel into a dark socket rim.
                Two shared defs for every head and no filters, so the plop
                stays a handful of circle nodes. Stops mirror $main-* in
                _colors.scss. */}
            <radialGradient id="grad-bolt" cx="0.37" cy="0.32" r="0.75">
              <stop offset="0" stopColor="#d6e8f4" />
              <stop offset="0.4" stopColor="#8697a0" />
              <stop offset="0.72" stopColor="#576971" />
              <stop offset="0.88" stopColor="#2c373b" />
              <stop offset="1" stopColor="#121a1c" />
            </radialGradient>
            <radialGradient id="grad-bolt-shadow" cx="0.5" cy="0.5" r="0.5">
              <stop offset="0" stopColor="#000000" stopOpacity="0.28" />
              <stop offset="0.62" stopColor="#000000" stopOpacity="0.2" />
              <stop offset="1" stopColor="#000000" stopOpacity="0" />
            </radialGradient>
            {/* Traveling glint band: a narrow gold stripe with transparent
                padding stops so the frame stays untouched between passes.
                Static here — the sweep motion is the rect's CSS transform,
                the SVG cousin of the cards' orbiting conic arc. */}
            <linearGradient
              id="grad-frame-sweep"
              x1="0"
              y1="0"
              x2="1"
              y2="0.22"
              gradientUnits="objectBoundingBox">
              <stop offset="0.4" stopColor="#f8bd24" stopOpacity="0" />
              <stop offset="0.47" stopColor="#f8bd24" stopOpacity="0.45" />
              <stop offset="0.5" stopColor="#ffeab6" stopOpacity="0.9" />
              <stop offset="0.53" stopColor="#f8bd24" stopOpacity="0.45" />
              <stop offset="0.6" stopColor="#f8bd24" stopOpacity="0" />
            </linearGradient>
            {/* Clip for the sweep rect: the same morphing frame outline the
                steel layer draws, so the glint only ever paints on metal. */}
            <clipPath id="frame-sweep-clip">
              <path
                d={mainFrame(
                  dimensions.w,
                  dimensions.h,
                  dimensions.corner,
                  dimensions.thick,
                  dimensions.thin,
                  dimensions.morph,
                )}
              />
            </clipPath>
          </defs>
        </svg>
      </div>
      <div className={styles["tab-body"]}>{currentTab.component}</div>
    </div>
  );
};
